/**
 * The site's route table.
 *
 * Drives the router, the header and footer navigation, the prerender step, the
 * sitemap and the breadcrumb structured data - so a new page is added once here
 * rather than in six places.
 */

export interface RouteDef {
  /** URL path, always with a leading slash and no trailing slash except root. */
  path: string;
  /** Label used in navigation and breadcrumbs. */
  label: string;
  /** Appears in the header's primary nav. */
  inPrimaryNav: boolean;
  /** Appears in the footer's "Legal" column. */
  inLegalNav: boolean;
  /** Relative weight in sitemap.xml. */
  priority: number;
  /**
   * ISO date this page's content last changed materially.
   *
   * Maintained by hand, and that is the point. The sitemap used to stamp every
   * URL with the build date, which told a crawler that all seven pages change
   * on every deploy - including deploys that only touched CSS. Google discounts
   * a `lastmod` it can see is automatic, so an inaccurate date is worth less
   * than no date at all. Bump this when you change what the page says.
   */
  updated: string;
}

export const ROUTES = [
  {
    path: '/',
    label: 'Home',
    inPrimaryNav: true,
    inLegalNav: false,
    priority: 1.0,
    updated: '2026-07-26',
  },
  {
    path: '/services',
    label: 'Services',
    inPrimaryNav: true,
    inLegalNav: false,
    priority: 0.9,
    updated: '2026-07-26',
  },
  {
    path: '/company',
    label: 'Company',
    inPrimaryNav: true,
    inLegalNav: false,
    priority: 0.8,
    updated: '2026-07-26',
  },
  {
    path: '/contact',
    label: 'Contact',
    inPrimaryNav: true,
    inLegalNav: false,
    priority: 0.8,
    updated: '2026-07-26',
  },
  /*
   * Payment & Refunds sits above the other legal pages, and is named for what a
   * reviewer is looking for rather than for what it legally is. Payment
   * providers and enterprise procurement both scan footer link text for the
   * word "refund"; "Terms of Use" does not read as a refund policy, and should
   * not have to.
   */
  {
    path: '/payment-and-refunds',
    label: 'Payment & Refunds',
    inPrimaryNav: false,
    inLegalNav: true,
    priority: 0.5,
    updated: '2026-07-26',
  },
  {
    path: '/privacy',
    label: 'Privacy Policy',
    inPrimaryNav: false,
    inLegalNav: true,
    priority: 0.3,
    updated: '2026-07-26',
  },
  {
    path: '/cookies',
    label: 'Cookie Policy',
    inPrimaryNav: false,
    inLegalNav: true,
    priority: 0.3,
    updated: '2026-07-26',
  },
  {
    path: '/terms',
    label: 'Terms of Use',
    inPrimaryNav: false,
    inLegalNav: true,
    priority: 0.3,
    updated: '2026-07-26',
  },
  {
    path: '/accessibility',
    label: 'Accessibility',
    inPrimaryNav: false,
    inLegalNav: true,
    priority: 0.3,
    updated: '2026-07-26',
  },
] as const satisfies readonly RouteDef[];

export type RoutePath = (typeof ROUTES)[number]['path'];

export const PRIMARY_NAV = ROUTES.filter((r) => r.inPrimaryNav);
export const LEGAL_NAV = ROUTES.filter((r) => r.inLegalNav);

export function routeLabel(path: string): string {
  return ROUTES.find((r) => r.path === path)?.label ?? '';
}

/** The ISO date a route's content last changed, for the sitemap and JSON-LD. */
export function routeUpdated(path: string): string | undefined {
  return ROUTES.find((r) => r.path === path)?.updated;
}
